import React, { createContext, useState, useEffect, ReactNode } from "react";

interface SocialMediaLink {
  platform: string;
  url: string;
}

interface VideoLink {
  title: string;
  url: string;
}

interface AdditionalField {
  key: string;
  value: string;
}

interface Certificate {
  title: string;
  details: string;
  linkUrl: string;
  imageUrl: string;
}

interface BgGradient {
  start: string;
  end: string;
  direction: string;
}

interface ProductData {
  headerLogo: string;
  headerLogoPosition: "left" | "center" | "right";
  bgColor: string;
  headingColor: string;
  textColor: string;
  keyBenefits: string[];
  backgroundImage: string;
  productName: string;
  category: string;
  productDescription: string;
  socialMediaLinks: SocialMediaLink[];
  videoLinks: VideoLink[];
  additionalFields: AdditionalField[];
  certificates: Certificate[];
  bgGradient?: BgGradient;
  mediaImages: string[];
  directionsToUse: string;
  careInstructions: string;
  productTaglines: string[];
  ingredients: string;
  fontStyle: string;
  headingFontStyle: string;
  brandDetails: string;
  composition: string;
  concentration: string;
  storageConditions: string;
  prescription: string;
  categoryType: string;
  alcoholContent: string;
  bottleSize: string;
  packagingType: string;
  shelfLife: string;
  warnings: string;
  apparelCategory: string;
  fabricType: string;
  sizeOptions: { [key: string]: boolean };
  colorOptions: string;
  pattern: string;
  dimensions: string;
  powerSource: string;
  capacity: string;
  safetyStandards: string;
  maintenance: string;
  BrandName: string;
  brandId: string;
}

interface ProductContextType {
  product: ProductData | null;
}

export const ProductContext = createContext<ProductContextType | undefined>(
  undefined
);

function decryptBase64(base64Str: string): string {
  const neededPadding = base64Str.length % 4;
  if (neededPadding) {
    base64Str += "====".slice(0, 4 - neededPadding);
  }
  return window.atob(base64Str);
}

export const ProductProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [product, setProduct] = useState<ProductData | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uidParam = params.get("uid") || "";

    // 1) decrypt if needed
    let finalUid: string;
    if (uidParam.startsWith("17")) {
      finalUid = uidParam;
    } else {
      finalUid = decryptBase64(uidParam);
    }

    // 2) extract SKU before the first "x"
    const sku = finalUid.split("x")[0];
    // console.log("Resolved SKU:", sku);

    const apiUrl = `https://5hv7gfeus4.execute-api.ap-south-1.amazonaws.com/prod/product-handler/?id=${sku}`;

    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        console.log(data); 
        if (Array.isArray(data) && data.length > 0) {
          setProduct(data[0]); 
        } else {
          setProduct(null);
        }
      })
      .catch((error) => console.error("Error fetching product data:", error));
  }, []);

  return (
    <ProductContext.Provider value={{ product }}>
      {children}
    </ProductContext.Provider>
  );
};
